import apiService from './api';

/**
 * Schema Service - Dynamically loads schemas from the backend API
 * 
 * This eliminates the need for the config/schemas.js file.
 * Instead, the backend introspects its Mongoose models and sends
 * the schema definitions to the frontend.
 */
class SchemaService {
  constructor() {
    this.schemas = {};
    this.modelList = [];
    this.isLoaded = false;
    this.loading = null;
  }

  /**
   * Load all models and their schemas from the API
   * This is called on app startup
   */
  async loadSchemas() {
    // If already loading, return the existing promise
    if (this.loading) {
      return this.loading;
    }

    // If already loaded, return immediately
    if (this.isLoaded) {
      return this.schemas;
    }

    // Start loading
    this.loading = this._performLoad();
    
    try {
      await this.loading;
      return this.schemas;
    } finally {
      this.loading = null;
    }
  }

  async _performLoad() {
    try {
      console.log('Loading schemas from API...');
      
      // Step 1: Get list of available models
      const modelsResponse = await apiService.getModels();
      this.modelList = modelsResponse.models || [];
      
      console.log(`Found ${this.modelList.length} models:`, this.modelList.map(m => m.name));

      // Step 2: Load schema for each model in parallel
      const schemaPromises = this.modelList.map(async (model) => {
        try {
          const schema = await apiService.getSchema(model.name);
          this.schemas[model.name] = schema;
          console.log(`✓ Loaded schema for ${model.name}`);
        } catch (error) {
          console.error(`✗ Error loading schema for ${model.name}:`, error);
        }
      });

      await Promise.all(schemaPromises);
      this.isLoaded = true;
      
      console.log('All schemas loaded successfully!');
      console.log('Available models:', Object.keys(this.schemas));
    } catch (error) {
      console.error('Error loading schemas from API:', error);
      throw new Error(`Failed to load schemas: ${error.message}`);
    }
  }

  /**
   * Get a specific schema by model name
   */
  getSchema(modelName) {
    return this.schemas[modelName];
  }

  /**
   * Get list of all available models
   */
  getModelList() {
    return this.modelList;
  }

  /**
   * Check if schemas have been loaded
   */
  isReady() {
    return this.isLoaded;
  }

  /**
   * Reload schemas from API
   * Useful if models are added/changed on the backend
   */
  async reload() {
    console.log('Reloading schemas...');
    this.schemas = {};
    this.modelList = [];
    this.isLoaded = false;
    this.loading = null;
    return this.loadSchemas();
  }
}

// Export singleton instance
export const schemaService = new SchemaService();

// Export convenience methods
export const loadSchemas = () => schemaService.loadSchemas();
export const getSchema = (modelName) => schemaService.getSchema(modelName);
export const getModelList = () => schemaService.getModelList();
export const isReady = () => schemaService.isReady();

export default schemaService;
